import React, { useState, useRef, useEffect, useMemo, useId } from 'react';
import {
  ChevronRight, ChevronDown, ChevronUp, Pencil,
  Boxes, Check, Settings2, AlertTriangle, Info,
} from 'lucide-react';
import Tooltip from '../Tooltip/Tooltip';
import { ActionMenu } from '../ActionMenu';
import { MoreVertical } from 'lucide-react';

// ── Tipos ───────────────────────────────────────────────────────────────────

export interface ProductUnit {
  id: string;
  name: string;
  abbr: string;
  /** Factor respecto a la unidad base (base = 1). Solo informativo. */
  factor: number;
}

export interface PriceType {
  id: string;
  name: string;
  hint?: string;
  /** Si true, la columna se muestra pero no es editable (ej. descuento/aumento global). */
  readOnly?: boolean;
}

export type CellPrice =
  | { mode: 'fixed'; value: number; expiry?: string }
  | { mode: 'empty' };

export interface PricedProduct {
  id: string;
  sku: string;
  name: string;
  /** Costo en unidad base; habilita el cálculo de margen. */
  cost?: number;
  baseUnitId: string;
  activeUnitId: string;
  units: ProductUnit[];
  /** Precios por columna y por unidad: prices[columnId][unitId] */
  prices: Record<string, Record<string, CellPrice>>;
}

export interface TableExpandableProps {
  products: PricedProduct[];
  priceTypes: PriceType[];
  /** unitId es la unidad a la que corresponde el precio guardado. */
  onPriceChange: (productId: string, priceTypeId: string, unitId: string, value: number | null) => void;
  onUnitChange?: (productId: string, unitId: string) => void;
  onAssign?: (productId: string) => void;
  onManage?: (productId: string) => void;
  density?: 'comfortable' | 'intermediate' | 'compact';
  showMargin?: boolean;
  sortable?: boolean;
  locale?: string;
  /** Símbolo de moneda que se muestra junto a los precios. Por defecto 'S/'. */
  currencySymbol?: string;
  /** Decimales soportados al editar y mostrar precios. Por defecto 2. */
  decimals?: number;
  emptyState?: React.ReactNode;
  className?: string;
}

// ── Constantes de layout ─────────────────────────────────────────────────────

const W_CHEVRON = 44;
const W_PRODUCT = 240;
const W_UNIT = 210;
const W_PRICE = 160;
const W_ACTIONS = 96;

// ── Utilidades ───────────────────────────────────────────────────────────────

const roundTo = (n: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * factor) / factor;
};
const toTitleCase = (s: string) => s.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
const factorOf = (units: ProductUnit[], id: string) => units.find(u => u.id === id)?.factor ?? 1;
const unitOf = (units: ProductUnit[], id: string) => units.find(u => u.id === id);

const MONTHS = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

function fmtMoney(n: number | null | undefined, locale = 'es-MX', decimals = 2) {
  if (n == null) return '';
  return n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: decimals });
}

type ExpiryState = 'ok' | 'soon' | 'expired';
function expiryStatus(iso?: string): { state: ExpiryState; label: string } | null {
  if (!iso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso + 'T00:00:00');
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  const label = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  if (days < 0) return { state: 'expired', label };
  if (days <= 30) return { state: 'soon', label };
  return { state: 'ok', label };
}

type MarginState = 'ok' | 'low' | 'loss';
function marginInfo(price: number | null, cost?: number): { pct: number; state: MarginState } | null {
  if (price == null || cost == null || price <= 0) return null;
  const pct = ((price - cost) / price) * 100;
  const state: MarginState = pct < 0 ? 'loss' : pct < 15 ? 'low' : 'ok';
  return { pct, state };
}

const MARGIN_CLASS: Record<MarginState, string> = {
  ok:   'text-success bg-success-bg',
  low:  'text-warning bg-warning-bg',
  loss: 'text-error bg-error-bg',
};

// ── Margen pill ──────────────────────────────────────────────────────────────

const MarginPill: React.FC<{ pct: number; state: MarginState; title?: string }> = ({ pct, state, title }) => (
  <span
    title={title}
    className={`rounded px-1.5 py-px text-micro font-semibold tabular-nums ${MARGIN_CLASS[state]}`}
  >
    {pct < 0 ? '−' : ''}{Math.abs(Math.round(pct))}%
  </span>
);

// ── Celda de precio (edición inline) ─────────────────────────────────────────

interface PriceCellProps {
  data: CellPrice;
  /** Costo ya ajustado a la unidad de esta celda. */
  cost?: number;
  showMargin: boolean;
  editing: boolean;
  seed: string | null;
  focused: boolean;
  locale: string;
  currencySymbol: string;
  decimals: number;
  onFocus: () => void;
  onStartEdit: () => void;
  onCommit: (value: number | null, nav: 'down' | 'right' | null) => void;
  onCancel: () => void;
}

const PriceCell: React.FC<PriceCellProps> = ({
  data, cost, showMargin, editing, seed, focused, locale, currencySymbol, decimals,
  onFocus, onStartEdit, onCommit, onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const priceCellId = useId();
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (editing) {
      const base = data.mode === 'fixed' ? String(roundTo(data.value, decimals)) : '';
      setDraft(seed != null ? seed : base);
      requestAnimationFrame(() => { inputRef.current?.focus(); if (seed == null) inputRef.current?.select(); });
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (editing) {
    const commit = (nav: 'down' | 'right' | null) => {
      const n = parseFloat(draft.replace(',', '.'));
      onCommit(isNaN(n) ? null : roundTo(n, decimals), nav);
    };
    return (
      <div className="-mx-2 -my-1.5 flex items-center gap-0.5 rounded-md border-[1.5px] border-brand bg-surface-0 px-2 py-1 shadow-[0_0_0_3px_var(--primary-light)]">
        <span className="text-tertiary text-caption">{currencySymbol}</span>
        <input
          id={`tabla-expandible-precio-input-${priceCellId}`}
          name={`tablaExpandablePrecioInput${priceCellId}`}
          ref={inputRef}
          value={draft}
          inputMode="decimal"
          className="w-[90px] border-0 bg-transparent font-sans text-ui-base font-semibold tabular-nums text-primary outline-none"
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commit('down'); }
            else if (e.key === 'Tab') { e.preventDefault(); commit('right'); }
            else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          onBlur={() => commit(null)}
        />
      </div>
    );
  }

  if (data.mode !== 'fixed') {
    return (
      <button
        id={`tabla-expandible-precio-editar-${priceCellId}`}
        name={`tablaExpandablePrecioEditar${priceCellId}`}
        type="button"
        onClick={() => { onFocus(); onStartEdit(); }}
        className={`inline-flex items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1.5 text-caption font-medium transition
          ${focused
            ? 'border-brand text-brand shadow-[0_0_0_3px_var(--primary-light)]'
            : 'border-strong text-muted hover:border-brand hover:bg-brand-light hover:text-brand'}`}
      >
        <Pencil size={12} strokeWidth={2.2} /> Editar
      </button>
    );
  }

  const mi = showMargin ? marginInfo(data.value, cost) : null;
  const st = expiryStatus(data.expiry);

  return (
    <button
      id={`tabla-expandible-precio-celda-${priceCellId}`}
      name={`tablaExpandablePrecioCelda${priceCellId}`}
      type="button"
      onClick={onFocus}
      onDoubleClick={onStartEdit}
      title="Doble clic o Enter para editar"
      className={`-mx-2 -my-1.5 flex w-full flex-col items-start gap-0.5 rounded-md border-[1.5px] px-2 py-1.5 text-left transition
        ${focused
          ? 'border-brand bg-surface-0 shadow-[0_0_0_3px_var(--primary-light)]'
          : 'border-transparent hover:border-muted hover:bg-surface-hover'}`}
    >
      <span className="flex items-center gap-2">
        <span className="text-ui-base font-semibold tabular-nums text-primary">
          <span className="mr-px text-caption text-muted">{currencySymbol}</span>{fmtMoney(data.value, locale, decimals)}
        </span>
        {mi && <MarginPill pct={mi.pct} state={mi.state} title={cost != null ? `Costo $${fmtMoney(cost, locale, decimals)}` : undefined} />}
      </span>
      {st && (
        <span className={`inline-flex items-center gap-1 text-label font-medium
          ${st.state === 'expired' ? 'text-error' : st.state === 'soon' ? 'text-warning' : 'text-muted'}`}>
          {st.state === 'expired' && <AlertTriangle size={10} strokeWidth={2.4} />}
          {st.state === 'soon' && <span className="inline-block h-1.5 w-1.5 rounded-full bg-warning" />}
          vence {st.label}
        </span>
      )}
    </button>
  );
};

// ── Selector de unidad ───────────────────────────────────────────────────────

const UnitSelect: React.FC<{
  product: PricedProduct; open: boolean; onToggle: () => void; onPick: (id: string) => void;
}> = ({ product, open, onToggle, onPick }) => {
  const active = unitOf(product.units, product.activeUnitId);
  const activeLabel = active?.name ?? 'Unidad no disponible';
  return (
    <div className="relative">
      <button
        id={`tabla-expandible-unidad-toggle-${product.id}`}
        name={`tablaExpandableUnidadToggle${product.id}`}
        type="button"
        onClick={onToggle}
        className={`flex h-8 w-fit min-w-[160px] items-center gap-1.5 rounded-md border bg-surface-0 px-2.5 text-primary transition
          ${open ? 'border-brand shadow-[0_0_0_3px_var(--primary-light)]' : 'border-strong hover:bg-surface-hover'}`}
      >
        <Boxes size={13} strokeWidth={2} className="shrink-0 text-muted" />
        <span className="min-w-0 flex-1 text-left text-caption-medium font-medium whitespace-nowrap">{activeLabel}</span>
        <ChevronDown size={13} strokeWidth={2.2} className="shrink-0 text-muted" />
      </button>
      {open && (
        <div className="absolute left-0 top-9 z-30 min-w-[180px] rounded-lg border border-default bg-surface-0 p-1 shadow-xl">
          {product.units.map(u => (
            <button
              id={`tabla-expandible-unidad-opcion-${product.id}-${u.id}`}
              name={`tablaExpandableUnidadOpcion${product.id}${u.id}`}
              type="button"
              key={u.id}
              onClick={() => onPick(u.id)}
              className={`flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 text-left text-ui-sm transition hover:bg-brand-light
                ${u.id === product.activeUnitId ? 'bg-brand-light' : ''}`}
            >
              <span className="flex items-center gap-2 font-medium text-primary">
                {u.name}
                {u.id === product.baseUnitId &&
                  <span className="rounded bg-brand-light px-1.5 py-px text-[8.5px] font-bold uppercase tracking-wide text-brand">base</span>}
              </span>
              <span className="flex items-center gap-1.5 text-label tabular-nums text-muted">
                {u.factor === 1 ? '1×' : `×${u.factor}`}
                {u.id === product.activeUnitId && <Check size={13} strokeWidth={2.6} className="text-brand" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Panel expandible: precios por unidad (editable) ──────────────────────────

interface UnitBreakdownProps {
  product: PricedProduct;
  priceTypes: PriceType[];
  locale: string;
  currencySymbol: string;
  decimals: number;
  showMargin: boolean;
  editing: EditCell | null;
  onStartEdit: (unitId: string, priceTypeId: string) => void;
  onCommit: (unitId: string, priceTypeId: string, value: number | null) => void;
  onCancel: () => void;
}

const UnitBreakdown: React.FC<UnitBreakdownProps> = ({
  product, priceTypes, locale, currencySymbol, decimals, showMargin, editing, onStartEdit, onCommit, onCancel,
}) => {
  const cols = `160px repeat(${priceTypes.length}, minmax(0, 1fr))`;
  return (
    <div className="bg-surface-2 py-2.5 pl-[60px] pr-5">
      <div className="mb-1.5 flex items-center gap-2 text-label font-semibold text-tertiary">
        Precios por unidad de medida
        {product.cost != null &&
          <span className="ml-auto font-normal text-muted tabular-nums">
            Costo base: <b className="font-semibold text-primary">${fmtMoney(product.cost, locale, decimals)}</b> / u
          </span>}
      </div>
      <div className="overflow-hidden rounded-lg border border-default bg-surface-0">
        <div className="grid items-center border-b border-muted bg-surface-1" style={{ gridTemplateColumns: cols }}>
          <div className="px-3.5 py-1.5 text-[10.5px] font-semibold text-tertiary">Unidad</div>
          {priceTypes.map(t => (
            <div key={t.id} className="px-3.5 py-1.5 text-[10.5px] font-semibold text-tertiary">{toTitleCase(t.name)}</div>
          ))}
        </div>
        {product.units.map(u => (
          <div key={u.id} className="grid items-center border-b border-muted last:border-b-0" style={{ gridTemplateColumns: cols }}>
            <div className="flex flex-col border-r border-muted px-3.5 py-2">
              <span className="flex items-center gap-1 text-caption-medium font-semibold text-primary">
                {u.name}
                {u.abbr && u.abbr !== u.name && (
                  <Tooltip text={u.abbr} position="top" size="sm">
                    <Info size={11} strokeWidth={2} className="cursor-help text-muted" />
                  </Tooltip>
                )}
              </span>
              <span className="text-[9.5px] text-muted">
                {u.id === product.baseUnitId ? 'unidad base' : `1 ${u.name.toLowerCase()} = ${u.factor} base`}
              </span>
            </div>
            {priceTypes.map(t => {
              const d = product.prices[t.id]?.[u.id] ?? { mode: 'empty' as const };
              const isEditing = !!editing
                && editing.source === 'breakdown'
                && editing.productId === product.id
                && editing.priceTypeId === t.id
                && editing.unitId === u.id;
              const costForUnit = product.cost != null ? product.cost * u.factor : undefined;
              return (
                <div key={t.id} className="px-3 py-1.5">
                  {t.readOnly ? (
                    <span className="tabular-nums text-caption-medium font-semibold text-secondary">
                      {d.mode === 'fixed'
                        ? <><span className="mr-px text-[9px] text-muted">{currencySymbol}</span>{fmtMoney(d.value, locale, decimals)}</>
                        : <span className="text-muted">—</span>}
                    </span>
                  ) : (
                    <PriceCell
                      data={d}
                      cost={costForUnit}
                      showMargin={showMargin}
                      editing={isEditing}
                      seed={isEditing ? editing!.seed ?? null : null}
                      focused={false}
                      locale={locale}
                      currencySymbol={currencySymbol}
                      decimals={decimals}
                      onFocus={() => {}}
                      onStartEdit={() => onStartEdit(u.id, t.id)}
                      onCommit={(val) => onCommit(u.id, t.id, val)}
                      onCancel={onCancel}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Componente principal ─────────────────────────────────────────────────────

interface SortState { col: string | null; dir: 'asc' | 'desc'; }
interface FocusCell { r: number; c: number; }
interface EditCell { productId: string; priceTypeId: string; unitId: string; source: 'main' | 'breakdown'; seed?: string | null; }

export const TableExpandable: React.FC<TableExpandableProps> = ({
  products,
  priceTypes,
  onPriceChange,
  onUnitChange,
  onAssign,
  onManage,
  density = 'comfortable',
  showMargin = true,
  sortable = true,
  locale = 'es-MX',
  currencySymbol = 'S/',
  decimals = 2,
  emptyState,
  className = '',
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditCell | null>(null);
  const [focus, setFocus] = useState<FocusCell | null>(null);
  const [unitOpen, setUnitOpen] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ col: null, dir: 'asc' });

  useEffect(() => {
    const close = () => { setUnitOpen(null); };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  const view = useMemo(() => {
    const list = products.slice();
    if (sortable && sort.col) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const key = (p: PricedProduct) => {
        const d = p.prices[sort.col!]?.[p.activeUnitId];
        return d && d.mode === 'fixed' ? d.value : (sort.dir === 'asc' ? Infinity : -Infinity);
      };
      list.sort((a, b) => (key(a) - key(b)) * dir);
    }
    return list;
  }, [products, sort, sortable]);

  const py = density === 'compact' ? 'py-1' : density === 'intermediate' ? 'py-1.5' : 'py-2.5';
  const minWidth = W_CHEVRON + W_PRODUCT + W_UNIT + W_PRICE * priceTypes.length + W_ACTIONS;

  const startEditFromKey = (r: number, c: number, seed?: string) => {
    const p = view[r]; if (!p) return;
    setEditing({ productId: p.id, priceTypeId: priceTypes[c].id, unitId: p.activeUnitId, source: 'main', seed });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (editing) return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (!focus) {
        if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) { setFocus({ r: 0, c: 0 }); e.preventDefault(); }
        return;
      }
      const { r, c } = focus;
      const lastR = view.length - 1, lastC = priceTypes.length - 1;
      if (e.key === 'ArrowDown') { e.preventDefault(); setFocus({ r: Math.min(lastR, r + 1), c }); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setFocus({ r: Math.max(0, r - 1), c }); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setFocus({ r, c: Math.min(lastC, c + 1) }); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setFocus({ r, c: Math.max(0, c - 1) }); }
      else if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); startEditFromKey(r, c); }
      else if (/^[0-9.]$/.test(e.key)) { e.preventDefault(); startEditFromKey(r, c, e.key); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focus, editing, view, priceTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitEdit = (r: number, c: number, unitId: string, value: number | null, nav: 'down' | 'right' | null) => {
    const p = view[r];
    onPriceChange(p.id, priceTypes[c].id, unitId, value);
    setEditing(null);
    if (nav === 'down') setFocus({ r: Math.min(view.length - 1, r + 1), c });
    else if (nav === 'right') setFocus({ r, c: Math.min(priceTypes.length - 1, c + 1) });
    else setFocus({ r, c });
  };

  const toggleSort = (tid: string) => setSort(s =>
    s.col !== tid ? { col: tid, dir: 'asc' } : s.dir === 'asc' ? { col: tid, dir: 'desc' } : { col: null, dir: 'asc' });

  const thBase = `sticky top-0 z-20 bg-surface-1 border-b border-default px-4 ${py} text-caption-medium font-sans text-tertiary whitespace-nowrap`;
  const tdBase = `px-4 ${py} border-b border-muted align-middle bg-surface-0 text-ui-sm font-sans font-medium text-primary`;

  return (
    <div className={`overflow-hidden rounded-lg border border-default bg-surface-0 ${className}`}>
      <div className="relative max-h-[560px] overflow-auto">
        <table className="w-full border-separate border-spacing-0" style={{ minWidth, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th className={`${thBase} sticky left-0 z-[21]`} style={{ width: W_CHEVRON, paddingLeft: 14, paddingRight: 0 }} />
              <th className={`${thBase} sticky z-[21] text-left sticky-sep-r`} style={{ minWidth: W_PRODUCT, left: 0 }}>Producto</th>
              <th className={`${thBase} text-left`} style={{ minWidth: W_UNIT }}>Unidad activa</th>
              {priceTypes.map(t => (
                <th key={t.id} className={`${thBase} text-left`} style={{ width: W_PRICE }}>
                  <button
                    id={`tabla-expandible-ordenar-${t.id}`}
                    name={`tablaExpandableOrdenar${t.id}`}
                    type="button"
                    onClick={sortable ? () => toggleSort(t.id) : undefined}
                    title={t.hint}
                    className={`relative inline-flex items-center gap-1 text-caption-medium font-semibold text-tertiary ${sortable ? 'cursor-pointer hover:text-brand' : 'cursor-default'}`}
                  >
                    {toTitleCase(t.name)}
                    {sort.col === t.id && (sort.dir === 'asc'
                      ? <ChevronUp size={12} strokeWidth={2.6} />
                      : <ChevronDown size={12} strokeWidth={2.6} />)}
                  </button>
                </th>
              ))}
              <th className={`${thBase} sticky right-0 z-[21] text-center sticky-sep-l`} style={{ width: W_ACTIONS }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {view.map((product, r) => {
              const isOpen = expanded === product.id;
              const activeUnit = unitOf(product.units, product.activeUnitId);
              const rowBg = isOpen ? 'bg-surface-1' : 'bg-surface-0';
              return (
                <React.Fragment key={product.id}>
                  <tr className="group transition-colors">
                    {/* chevron */}
                    <td className={`${tdBase} sticky left-0 z-[2] ${rowBg} group-hover:bg-surface-hover`} style={{ width: W_CHEVRON, paddingLeft: 14, paddingRight: 0 }}>
                      <button
                        id={`tabla-expandible-expandir-${product.id}`}
                        name={`tablaExpandableExpandir${product.id}`}
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : product.id)}
                        aria-label="Expandir"
                        className={`grid h-[26px] w-[26px] place-items-center rounded-md transition
                          ${isOpen ? 'rotate-90 bg-brand-light text-brand' : 'text-muted hover:bg-surface-hover hover:text-secondary'}`}
                      >
                        <ChevronRight size={16} strokeWidth={2.4} />
                      </button>
                    </td>

                    {/* producto */}
                    <td className={`${tdBase} sticky z-[2] sticky-sep-r ${rowBg} group-hover:bg-surface-hover`} style={{ minWidth: W_PRODUCT, left: 0 }}>
                      <div className="flex flex-col gap-px">
                        <span className="text-label font-semibold text-muted">{product.sku}</span>
                        <span className="text-ui-sm font-semibold leading-tight text-primary">{product.name}</span>
                      </div>
                    </td>

                    {/* unidad */}
                    <td className={`${tdBase} ${rowBg} group-hover:bg-surface-hover`} style={{ minWidth: W_UNIT }} onClick={e => e.stopPropagation()}>
                      <UnitSelect
                        product={product}
                        open={unitOpen === product.id}
                        onToggle={() => setUnitOpen(unitOpen === product.id ? null : product.id)}
                        onPick={uid => { onUnitChange?.(product.id, uid); setUnitOpen(null); }}
                      />
                      {activeUnit && activeUnit.factor !== 1 &&
                        <span className="mt-0.5 block pl-0.5 text-[10px] tabular-nums text-muted">×{activeUnit.factor} sobre base</span>}
                    </td>

                    {/* precios */}
                    {priceTypes.map((t, c) => {
                      const d = product.prices[t.id]?.[product.activeUnitId] ?? { mode: 'empty' as const };
                      const isEditing = !!editing
                        && editing.source === 'main'
                        && editing.productId === product.id
                        && editing.priceTypeId === t.id
                        && editing.unitId === product.activeUnitId;
                      const isFocused = !!focus && focus.r === r && focus.c === c;
                      const costForUnit = product.cost != null
                        ? product.cost * (activeUnit?.factor ?? 1)
                        : undefined;
                      return (
                        <td key={t.id} className={`${tdBase} ${rowBg} group-hover:bg-surface-hover`} style={{ width: W_PRICE }}>
                          {t.readOnly ? (
                            <span className="tabular-nums text-ui-base font-semibold text-secondary">
                              {d.mode === 'fixed'
                                ? <><span className="mr-px text-caption text-muted">{currencySymbol}</span>{fmtMoney(d.value, locale, decimals)}</>
                                : <span className="text-caption text-muted">—</span>}
                            </span>
                          ) : (
                            <PriceCell
                              data={d}
                              cost={costForUnit}
                              showMargin={showMargin}
                              editing={isEditing}
                              seed={isEditing ? editing!.seed ?? null : null}
                              focused={isFocused}
                              locale={locale}
                              currencySymbol={currencySymbol}
                              decimals={decimals}
                              onFocus={() => setFocus({ r, c })}
                              onStartEdit={() => setEditing({ productId: product.id, priceTypeId: t.id, unitId: product.activeUnitId, source: 'main' })}
                              onCommit={(val, nav) => commitEdit(r, c, product.activeUnitId, val, nav)}
                              onCancel={() => { setEditing(null); setFocus({ r, c }); }}
                            />
                          )}
                        </td>
                      );
                    })}

                    {/* acciones */}
                    <td className={`${tdBase} sticky right-0 z-[2] text-center sticky-sep-l ${rowBg} group-hover:bg-surface-hover`} style={{ width: W_ACTIONS }} onClick={e => e.stopPropagation()}>
                      <ActionMenu>
                        <ActionMenu.Trigger>
                          <MoreVertical size={17} />
                        </ActionMenu.Trigger>
                        <ActionMenu.Content>
                          {onAssign && (
                            <ActionMenu.Item icon={<Pencil size={14} />} onClick={() => onAssign(product.id)}>
                              Asignar / editar precios
                            </ActionMenu.Item>
                          )}
                          {onManage && (
                            <ActionMenu.Item icon={<Settings2 size={14} />} onClick={() => onManage(product.id)}>
                              Ver resumen
                            </ActionMenu.Item>
                          )}
                        </ActionMenu.Content>
                      </ActionMenu>
                    </td>
                  </tr>

                  {isOpen && (
                    <tr>
                      <td colSpan={4 + priceTypes.length} className="border-b border-muted p-0">
                        <UnitBreakdown
                          product={product}
                          priceTypes={priceTypes}
                          locale={locale}
                          currencySymbol={currencySymbol}
                          decimals={decimals}
                          showMargin={showMargin}
                          editing={editing}
                          onStartEdit={(unitId, priceTypeId) =>
                            setEditing({ productId: product.id, priceTypeId, unitId, source: 'breakdown' })
                          }
                          onCommit={(unitId, priceTypeId, val) => {
                            onPriceChange(product.id, priceTypeId, unitId, val);
                            setEditing(null);
                          }}
                          onCancel={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {view.length === 0 && (
              <tr>
                <td colSpan={4 + priceTypes.length} className="px-4 py-9 text-center text-ui-sm text-muted">
                  {emptyState ?? 'No hay productos para mostrar.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TableExpandable.displayName = 'TableExpandable';

export default TableExpandable;
